import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, FileText, Download } from 'lucide-react';
import { format } from 'date-fns';

// Lists the credit notes issued against an order, with download links to the
// branded PDF. Shown on the order's Refund tab above the refund form. Reloads
// when `refreshSignal` changes (incremented after a refund is issued).

export default function CreditNotesPanel({ orderId, refreshSignal = 0 }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await base44.entities.CreditNote.filter({ order_id: orderId }, '-created_date', 50);
      setNotes(rows || []);
    } catch (_e) {
      setNotes([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, refreshSignal]);

  if (loading) {
    return <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-white/30" /></div>;
  }
  if (!notes.length) return null;

  return (
    <div className="mb-4 space-y-2">
      <p className="text-primary text-[10px] font-bold tracking-widest">CREDIT NOTES ISSUED</p>
      {notes.map((cn) => (
        <div key={cn.id} className="flex items-center gap-3 border border-white/10 bg-white/[0.02] px-3 py-2">
          <FileText className="w-3.5 h-3.5 text-white/40 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-white/80 text-xs font-mono font-bold">{cn.credit_note_number}</p>
            <p className="text-white/40 text-[10px] truncate">{cn.reason || '—'}</p>
          </div>
          <span className="text-white/70 text-xs font-bold whitespace-nowrap">-£{Number(cn.amount || 0).toFixed(2)}</span>
          <span className="text-white/30 text-[10px] hidden sm:block whitespace-nowrap">
            {cn.created_date ? format(new Date(cn.created_date), 'dd MMM yy') : ''}
          </span>
          {cn.pdf_url && (
            <a
              href={cn.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-primary hover:text-primary/80 text-[10px] font-bold tracking-widest transition-colors whitespace-nowrap"
            >
              <Download className="w-3 h-3" /> PDF
            </a>
          )}
        </div>
      ))}
    </div>
  );
}